import { useRef } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Stack, TextField, Divider, Typography, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import fetchDeviceList from '../../utils/fetchDeviceList';
import fetchStreamList from '../../utils/fetchStreamList';
import { appSliceActions } from '../../appSlices/appSlice';
import axiosInstance from '../../utils/axiosInstance';

// ----------------------------------------------------------------------

export default function CameraNetworkInfo(props) {
  const { networkInfo, camera } = props;
  console.log('props: ', networkInfo, camera);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const dhcpV4Ref = useRef();
  const dhcpV6Ref = useRef();
  const ipAddressV4Ref = useRef();
  const ipAddressV6Ref = useRef();
  const isIpv4EnabledRef = useRef();
  const isIpv6EnabledRef = useRef();
  const subnetMaskV4Ref = useRef();
  const subnetMaskV6Ref = useRef();

  const handleSubmit = () => {
    enqueueSnackbar(`Pending - update ${camera.name} info`, {
      variant: 'info',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });
    const jsonData = {
      dhcpV4: dhcpV4Ref.current.value,
      dhcpV6: dhcpV6Ref.current.value,
      ipAddressV4: ipAddressV4Ref.current.value,
      ipAddressV6: ipAddressV6Ref.current.value,
      isIpv4Enabled: isIpv4EnabledRef.current.value === "true" || isIpv4EnabledRef.current.value === true,
      isIpv6Enabled: isIpv6EnabledRef.current.value === "true" || isIpv4EnabledRef.current.value === true,
      subnetMaskV4: subnetMaskV4Ref.current.value,
      subnetMaskV6: subnetMaskV6Ref.current.value,
    };
    axiosInstance
      .post(`${window.location.protocol}//${camera.host}/api/v1/sensor/${camera.sensorId}/network`, jsonData)
      .then(async (response) => {
        enqueueSnackbar(`Success - ${camera.name} info updated`, {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        if (response.data && response.data.rebootNeeded) {
          enqueueSnackbar(`Success - ${camera.name} rebooting device`, {
            variant: 'success',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
          axiosInstance
          .post(`${window.location.protocol}//${camera.host}/api/v1/sensor/${camera.sensorId}/reboot`, jsonData)
          .then(async () => {
            enqueueSnackbar(`Success - ${camera.name} reboot request sent to device`, {
              variant: 'success',
              anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            });
          })
          .catch(() => {
            enqueueSnackbar(`Error - Failed to reboot ${camera.name}`, {
              variant: 'error',
              anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            })})
        }
        const cameraList = await fetchDeviceList(camera.host);
        if (cameraList != null) {
          for (let i = 0; i < cameraList.length; i += 1) {
            cameraList[i].host = camera.host;
          }
          dispatch(appSliceActions.addDevice(cameraList));
        }
        const streamList = await fetchStreamList(camera.host);
        if (streamList != null) {
          for (let i = 0; i < streamList.length; i += 1) {
            streamList[i].host = camera.host;
          }
          dispatch(appSliceActions.addStream(streamList));
        }
      })
      .catch((error) => {
        enqueueSnackbar(`Error - Failed to set ${camera.name} info`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        if (error.response.data == null)
          enqueueSnackbar(`Error - ${error.response.status}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        else
          enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
      });
  };

  return (
    <>
      <Stack spacing={3} sx={{ mb: 2 }}>
        {networkInfo != null && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                IP v4 options
              </Typography>
            </Divider>
            <TextField
              name="isIpv4Enabled"
              label="is IPv4 enabled ?"
              defaultValue={networkInfo.isIpv4Enabled}
              inputRef={isIpv4EnabledRef}
            />
            <TextField name="dhcpV4" label="dhcpV4" defaultValue={networkInfo.dhcpV4} inputRef={dhcpV4Ref} />
            <TextField
              name="ipAddressV4"
              label="IP address v4"
              defaultValue={networkInfo.ipAddressV4}
              inputRef={ipAddressV4Ref}
            />
            <TextField
              name="subnetMaskV4"
              label="subnet mask v4"
              defaultValue={networkInfo.subnetMaskV4}
              inputRef={subnetMaskV4Ref}
            />
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                IP v6 options
              </Typography>
            </Divider>
            <TextField
              name="isIpv6Enabled"
              label="is IPv6 enabled ?"
              defaultValue={networkInfo.isIpv6Enabled}
              inputRef={isIpv6EnabledRef}
            />
            <TextField name="dhcpV6" label="dhcpV6" defaultValue={networkInfo.dhcpV6} inputRef={dhcpV6Ref} />
            <TextField
              name="ipAddressV6"
              label="IP address v6"
              defaultValue={networkInfo.ipAddressV6}
              inputRef={ipAddressV6Ref}
            />
            <TextField
              name="subnetMaskV6"
              label="subnet mask v6"
              defaultValue={networkInfo.subnetMaskV6}
              inputRef={subnetMaskV6Ref}
            />
          </>
        )}
      </Stack>
      {networkInfo != null && (
        <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
          <LoadingButton size="large" type="submit" variant="contained" onClick={handleSubmit}>
            Submit
          </LoadingButton>
        </Box>
      )}
    </>
  );
}

CameraNetworkInfo.propTypes = {
  networkInfo: PropTypes.shape({
    dhcpV4: PropTypes.string.isRequired,
    dhcpV6: PropTypes.string.isRequired,
    ipAddressV4: PropTypes.string.isRequired,
    ipAddressV6: PropTypes.string.isRequired,
    isIpv4Enabled: PropTypes.bool.isRequired,
    isIpv6Enabled: PropTypes.bool.isRequired,
    subnetMaskV4: PropTypes.string.isRequired,
    subnetMaskV6: PropTypes.string.isRequired,
  }).isRequired,
  camera: PropTypes.shape({
    sensorId: PropTypes.string.isRequired,
    host: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};
